<template>
  <div class="content-text">
    <h2 class="title-2 text-left">10 Conclusão</h2>

    <p>
      No período de abril de 2024 a maio de 2025,
      <b>{{ nomeMunicipio }}</b
      >, não terá perda de recursos federais no cofinanciamento da APS devido a
      classificação automática <b>“BOM”</b> que recebeu.
    </p>

    <div class="resumo-equipes-simulacao">
      <div
        v-for="card in cardsResumo"
        :key="card.key"
        class="resumo-card"
        :class="card.key"
      >
        <div class="resumo-card__top"></div>

        <div class="resumo-card__body">
          <h4>{{ card.title }}</h4>

          <div
            v-for="item in card.itens"
            :key="`${card.key}-${item.label}`"
            class="resumo-card__item"
          >
            <div class="resumo-card__info">
              <span class="qtd">{{ item.quantidade }}</span>
              <strong>{{ item.label }}</strong>
            </div>

            <span class="valor"> = {{ formatCurrency(item.valorTotal) }} </span>
          </div>

          <div class="resumo-card__total">
            <span>=</span>
            <strong>{{ formatCurrency(card.total) }}</strong>
          </div>
          <div v-if="card.key !== 'otima'" class="simulador-perda">
            <span>vs Ótimo</span>

            <strong> - {{ formatCurrency(perdaCard(card)) }} </strong>
          </div>
        </div>
      </div>
    </div>

    <p>
      A avaliação oficial do resultado de desempenho olha para dados
      retroativos, ou seja, quando acontecer em maio de 2025, como está
      prevista, tende a analisar dados a partir de maio de 2024. Isso implica em
      ações imediatas que sejam aderentes ao novo modelo de cofinanciamento
      federal da APS.
    </p>

    <p>
      Entende-se que o valor de
      <b
        >R$
        {{
          diferencaMensalOtimoRegular.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })
        }}
      </b>
      no repasse mensal ou
      <b
        >R$
        {{
          diferencaAnualOtimoRegular.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })
        }}
      </b>
      considerando o repasse anual (diferença entre o desempenho
      <b>"ÓTIMO"</b> e <b>"REGULAR"</b>) é representativo para o município
      manter os serviços da APS com qualidade.
    </p>

    <p>
      Para o desempenho <b>"ÓTIMO"</b>, faz-se necessário o trabalho conjunto e
      colaborativo entre a equipe gestora, profissionais de informática,
      monitoramento e avaliação e os profissionais de saúde atuando nas equipes
      (eSF, ACS, eAP, eSB, eMulti) de forma integrada no cuidado continuo aos
      cidadãos do território.
    </p>
  </div>
</template>

<script>
import { regras, getLabelRegra } from '../utils.js'

export default {
  props: {
    dados: {
      type: Object,
      default: () => {},
    },
    nomeMunicipio: {
      type: String,
      default: '',
    },
    diferencaMensalOtimoRegular: {
      type: Number,
      default: 0,
    },
    diferencaAnualOtimoRegular: {
      type: Number,
      default: 0,
    },
    fonte: {
      type: String,
      default: '',
    },
  },
  data() {
    return {}
  },
  mounted() {},
  computed: {
    cardsResumo() {
      const ordem = ['regular', 'suficiente', 'boa', 'otima']

      const map = {
        regular: 'Regular',
        suficiente: 'Suficiente',
        boa: 'Bom',
        otima: 'Ótimo',
      }

      const filtrosAtivos = this.getFiltrosAtivos()

      return ordem
        .map((key) => {
          const itensAgrupados = {}

          ;(this.dados?.etapa4?.equipes || []).forEach((item) => {
            if (item.classificacao !== key) {
              return
            }

            const grupo =
              item.componente === 'CVAT'
                ? 'CVAT'
                : this.getFiltroDaEquipe(item.equipe)

            if (!filtrosAtivos.includes(grupo) && grupo !== 'CVAT') {
              return
            }

            if (!itensAgrupados[grupo]) {
              itensAgrupados[grupo] = {
                label: grupo,
                quantidade: 0,
                valorTotal: 0,
                perdaTotal: 0,
              }
            }

            const quantidade = Number(item.quantidade || 0)
            const valorTotal = Number(item.valorTotal || 0)
            const valorOtimo = this.getValorOtimoItem(item)

            itensAgrupados[grupo].quantidade += quantidade
            itensAgrupados[grupo].valorTotal += valorTotal
            itensAgrupados[grupo].perdaTotal += Math.max(
              0,
              quantidade * valorOtimo - valorTotal
            )
          })

          const itens = Object.values(itensAgrupados)

          const total = itens.reduce((total, item) => {
            return total + Number(item.valorTotal || 0)
          }, 0)

          return {
            key,
            title: map[key],
            itens,
            total,
          }
        })
        .filter((card) => {
          return card.itens.some((item) => Number(item.quantidade || 0) > 0)
        })
    },
  },
  methods: {
    /**
     * Retorna qual filtro pertence a equipe.
     *
     * @param {String} equipe
     * @returns {String}
     */
    getFiltroDaEquipe(equipe) {
      if (['eSF', 'eSFR'].includes(equipe)) {
        return 'eSF'
      }

      if (equipe.includes('eAP')) {
        return 'eAP'
      }

      if (equipe.includes('eSB')) {
        return 'eSB'
      }

      if (['Estratégica', 'Complementar', 'Ampliada'].includes(equipe)) {
        return 'eMulti'
      }

      if (equipe.includes('eCR')) {
        return 'eCR'
      }

      if (
        [
          'Essencial 20h',
          'Essencial 30h',
          'Ampliada 20h',
          'Ampliada 30h',
        ].includes(equipe)
      ) {
        return 'eAPP'
      }

      return ''
    },

    /**
     * Retorna os filtros ativos da etapa 4.
     *
     * @returns {Array}
     */
    getFiltrosAtivos() {
      const filtros = this.dados?.etapa4?.filtros || {}

      return [
        'CVAT',
        ...Object.keys(filtros).filter((key) => {
          return key !== 'CVAT' && filtros[key] === true
        }),
      ]
    },

    /**
     * Formata um valor em moeda brasileira.
     *
     * @param {Number} value
     * @returns {String}
     */
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(value || 0))
    },

    /**
     * Retorna o valor ótimo do item.
     *
     * @param {Object} item
     * @returns {Number}
     */
    getValorOtimoItem(item) {
      const grupoRegras =
        item.componente === 'CVAT'
          ? regras.vinculo_e_acompanhamento
          : regras.qualidade

      const regra = grupoRegras.find((regra) => {
        return regra.label === getLabelRegra(item.equipe)
      })

      if (!regra) {
        return 0
      }

      const classificacaoOtima = regra.classificacao.find((classificacao) => {
        return classificacao.tipo === 'Ótimo'
      })
      return Number(classificacaoOtima?.valor || 0)
    },

    /**
     * Calcula a perda do card em relação ao ótimo.
     *
     * @param {Object} card
     * @returns {Number}
     */
    perdaCard(card) {
      return card.itens.reduce((total, item) => {
        return total + Number(item.perdaTotal || 0)
      }, 0)
    },
  },
}
</script>

<style lang="scss">
.resumo-equipes-simulacao {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
  margin: 20px 0;
}

.resumo-card {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 35px rgba(15, 23, 42, 0.12);
}

.resumo-card__top {
  height: 10px;
}

.resumo-card.regular .resumo-card__top {
  background: #c46500;
}

.resumo-card.suficiente .resumo-card__top {
  background: #d5ba0a;
}

.resumo-card.boa .resumo-card__top {
  background: #00a878;
}

.resumo-card.otima .resumo-card__top {
  background: #2f80b7;
}

.resumo-card__body {
  padding: 18px;
}

.resumo-card__body h4 {
  margin: 0 0 18px;
  text-align: center;
  font-size: 14px;
  font-weight: 800;
}

.resumo-card__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid #edf2f7;
  font-size: 12px;
}

.resumo-card__info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.resumo-card__info .qtd {
  padding: 6px 10px;
  border: 1px solid #d7e8ff;
  border-radius: 9px;
  background: #eff6ff;
  color: #2b6cb0;
  font-weight: 800;
  text-align: center;
}

.resumo-card__item .valor {
  white-space: nowrap;
  font-weight: 800;
  color: #0f172a;
}

.resumo-card__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding: 14px;
  border-radius: 12px;
  background: #f8fafc;
  font-weight: 800;
  font-size: 12px;
}

.simulador-perda,
.simulador-otimo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 12px;
  padding: 12px;
  margin-top: 10px;
  font-weight: 800;
  font-size: 12px;
}

.simulador-perda {
  background: #fef2f2;
  color: #dc2626;
}

.simulador-otimo {
  background: #f0fdf4;
  color: #15803d;
}

.simulador-otimo i {
  font-size: 16px;
}

@media print {
  .resumo-equipes-simulacao {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 12px !important;
    margin: 12px 0 18px !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .resumo-card {
    background: #ffffff !important;
    border: 1px solid #dbe2ea !important;
    border-radius: 16px !important;
    box-shadow: none !important;
    overflow: hidden !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .resumo-card__top,
  .resumo-card.regular .resumo-card__top,
  .resumo-card.suficiente .resumo-card__top,
  .resumo-card.boa .resumo-card__top,
  .resumo-card.otima .resumo-card__top {
    height: 10px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .resumo-card.regular .resumo-card__top {
    background-color: #c46500 !important;
  }

  .resumo-card.suficiente .resumo-card__top {
    background-color: #d5ba0a !important;
  }

  .resumo-card.boa .resumo-card__top {
    background-color: #00a878 !important;
  }

  .resumo-card.otima .resumo-card__top {
    background-color: #2f80b7 !important;
  }

  .resumo-card__body {
    padding: 12px !important;
  }

  .resumo-card__body h4 {
    font-size: 13px !important;
    margin-bottom: 10px !important;
  }

  .resumo-card__item {
    padding: 7px 0 !important;
    font-size: 10px !important;
    border-bottom: 1px solid #edf2f7 !important;
  }

  .resumo-card__info .qtd {
    background-color: #eff6ff !important;
    border: 1px solid #d7e8ff !important;
    color: #2b6cb0 !important;
    border-radius: 8px !important;
    padding: 4px 8px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .resumo-card__total {
    background-color: #f8fafc !important;
    border-radius: 10px !important;
    padding: 10px !important;
    font-size: 10px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .simulador-perda {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .simulador-otimo {
    background-color: #f0fdf4 !important;
    color: #15803d !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
